import { Conversation, ChatMessage, TeamConversation, TeamMessage } from '../../model/chat/Chat';
import i18n from "../../i18n";

const API_URL = process.env.REACT_APP_CHAT_SERVICE_BASE_URL || '';

/**
 * Fetch all conversations for a business.
 * businessId is a GraphQL ID — coerced to Number before sending to avoid the
 * known ID-vs-Int mismatch in this project.
 */
export async function getBusinessConversations(
    businessId: number | string,
    token: string
): Promise<Conversation[]> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `query businessConversations($businessId: ID!) {
                businessConversations(businessId: $businessId) {
                    id businessId businessSlug businessName businessImage
                    clientId clientName clientEmail clientAvatar
                    lastMessageText lastMessageAt lastSenderType
                    unreadForBusiness unreadForClient
                }
            }`,
            variables: { businessId: Number(businessId) },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.businessConversations as Conversation[];
}

/** Fetch all messages for a conversation thread. */
export async function getConversationMessages(
    conversationId: string,
    token: string
): Promise<ChatMessage[]> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `query conversationMessages($conversationId: ID!) {
                conversationMessages(conversationId: $conversationId) {
                    id conversationId senderType text imageKeys imageUrls createdAt
                }
            }`,
            variables: { conversationId },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.conversationMessages as ChatMessage[];
}

/** Returns the total count of unread messages for the business (for badge display). */
export async function getBusinessUnreadCount(
    businessId: number | string,
    token: string
): Promise<number> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `query businessUnreadCount($businessId: ID!) {
                businessUnreadCount(businessId: $businessId)
            }`,
            variables: { businessId: Number(businessId) },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.businessUnreadCount as number;
}

/**
 * Send a message from the business side.
 * Pass text and/or imageKeys; at least one must be present on the backend.
 */
export async function sendBusinessMessage(
    conversationId: string,
    text: string | null,
    imageKeys: string[],
    token: string
): Promise<ChatMessage> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `mutation sendBusinessMessage($conversationId: ID!, $text: String, $imageKeys: [String!]) {
                sendBusinessMessage(conversationId: $conversationId, text: $text, imageKeys: $imageKeys) {
                    id conversationId senderType text imageKeys imageUrls createdAt
                }
            }`,
            variables: {
                conversationId,
                text: text || null,
                imageKeys: imageKeys.length > 0 ? imageKeys : null,
            },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.sendBusinessMessage as ChatMessage;
}

/**
 * Ask chat-service for a presigned PUT URL + the object key for a chat image upload.
 * conversationId is optional — when present the backend files the image under
 * chat/<businessId>/<clientEmail>/ instead of a generic path.
 */
export async function createChatUploadUrl(
    contentType: string,
    token: string,
    conversationId?: string | null
): Promise<{ uploadUrl: string; key: string }> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `mutation createChatUploadUrl($contentType: String!, $conversationId: ID) {
                createChatUploadUrl(contentType: $contentType, conversationId: $conversationId) { uploadUrl key }
            }`,
            variables: { contentType, conversationId: conversationId ?? null },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.createChatUploadUrl as { uploadUrl: string; key: string };
}

/**
 * Full 3-step upload flow for a chat image:
 * 1. Presign via chat-service's createChatUploadUrl mutation.
 * 2. PUT the file directly to R2 (no Authorization header — presigned URL).
 * 3. Return the key to embed in imageKeys.
 * conversationId is optional — forwarded to createChatUploadUrl for proper path filing.
 */
export async function uploadChatImage(
    file: File,
    token: string,
    conversationId?: string | null
): Promise<string> {
    const { uploadUrl, key } = await createChatUploadUrl(file.type, token, conversationId);
    const put = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
    });
    if (!put.ok) throw new Error(`Falha no upload da imagem (${put.status})`);
    return key;
}

/**
 * Start a conversation from the business side with a known client.
 * businessId is coerced to Number to avoid the ID-vs-Int mismatch.
 * clientName, businessName, businessImage, businessSlug are optional metadata
 * the chat-service uses to pre-populate the conversation record.
 */
export interface StartConversationArgs {
    businessId: number | string;
    clientEmail: string;
    clientName?: string | null;
    businessName?: string | null;
    businessImage?: string | null;
    businessSlug?: string | null;
}

export async function startConversationWithClient(
    args: StartConversationArgs,
    token: string
): Promise<Conversation> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `mutation startConversationWithClient(
                $businessId: ID!,
                $clientEmail: String!,
                $clientName: String,
                $businessName: String,
                $businessImage: String,
                $businessSlug: String
            ) {
                startConversationWithClient(
                    businessId: $businessId,
                    clientEmail: $clientEmail,
                    clientName: $clientName,
                    businessName: $businessName,
                    businessImage: $businessImage,
                    businessSlug: $businessSlug
                ) {
                    id businessId businessSlug businessName businessImage
                    clientId clientName clientEmail clientAvatar
                    lastMessageText lastMessageAt lastSenderType
                    unreadForBusiness unreadForClient
                }
            }`,
            variables: {
                businessId: Number(args.businessId),
                clientEmail: args.clientEmail,
                clientName: args.clientName ?? null,
                businessName: args.businessName ?? null,
                businessImage: args.businessImage ?? null,
                businessSlug: args.businessSlug ?? null,
            },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.startConversationWithClient as Conversation;
}

/** Mark all messages in a conversation as read by the business. */
export async function markConversationReadByBusiness(
    conversationId: string,
    token: string
): Promise<boolean> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `mutation markConversationReadByBusiness($conversationId: ID!) {
                markConversationReadByBusiness(conversationId: $conversationId)
            }`,
            variables: { conversationId },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.markConversationReadByBusiness as boolean;
}

/* =========================================================
   Team-DM functions (internal 1:1 between team members)
   ========================================================= */

/** Fetch all team DM conversations for the caller within a business. */
export async function myTeamConversations(
    businessId: number | string,
    token: string
): Promise<TeamConversation[]> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `query myTeamConversations($businessId: ID!) {
                myTeamConversations(businessId: $businessId) {
                    id businessId otherEmail otherName lastMessage lastAt unread
                }
            }`,
            variables: { businessId: Number(businessId) },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.myTeamConversations as TeamConversation[];
}

/** Fetch messages for a team DM thread. */
export async function teamConversationMessages(
    conversationId: string,
    token: string
): Promise<TeamMessage[]> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `query teamConversationMessages($conversationId: ID!) {
                teamConversationMessages(conversationId: $conversationId) {
                    id conversationId senderEmail senderName text imageKeys imageUrls createdAt mine
                }
            }`,
            variables: { conversationId },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.teamConversationMessages as TeamMessage[];
}

/** Returns the total count of unread team DMs for the caller within a business. */
export async function teamUnreadCount(
    businessId: number | string,
    token: string
): Promise<number> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `query teamUnreadCount($businessId: ID!) {
                teamUnreadCount(businessId: $businessId)
            }`,
            variables: { businessId: Number(businessId) },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.teamUnreadCount as number;
}

/**
 * Start (or resume) a 1:1 team DM with another team member.
 * Returns the existing conversation if one already exists.
 */
export async function startTeamConversation(
    businessId: number | string,
    otherEmail: string,
    token: string
): Promise<TeamConversation> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `mutation startTeamConversation($businessId: ID!, $otherEmail: String!) {
                startTeamConversation(businessId: $businessId, otherEmail: $otherEmail) {
                    id businessId otherEmail otherName lastMessage lastAt unread
                }
            }`,
            variables: { businessId: Number(businessId), otherEmail },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.startTeamConversation as TeamConversation;
}

/** Send a message in a team DM thread. */
export async function sendTeamMessage(
    conversationId: string,
    text: string | null,
    imageKeys: string[],
    token: string
): Promise<TeamMessage> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `mutation sendTeamMessage($conversationId: ID!, $text: String, $imageKeys: [String!]) {
                sendTeamMessage(conversationId: $conversationId, text: $text, imageKeys: $imageKeys) {
                    id conversationId senderEmail senderName text imageKeys imageUrls createdAt mine
                }
            }`,
            variables: {
                conversationId,
                text: text || null,
                imageKeys: imageKeys.length > 0 ? imageKeys : null,
            },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.sendTeamMessage as TeamMessage;
}

/** Mark all messages in a team DM conversation as read by the caller. */
export async function markTeamConversationRead(
    conversationId: string,
    token: string
): Promise<boolean> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `mutation markTeamConversationRead($conversationId: ID!) {
                markTeamConversationRead(conversationId: $conversationId)
            }`,
            variables: { conversationId },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.markTeamConversationRead as boolean;
}
